// Entry diagnostic: a guided two-set sampler (Listening P1 + Reading P3) that
// reads the student's results and recommends where to start. It recommends,
// never schedules: the student drives pace.
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Circle } from 'lucide-react';

import { api } from '@/lib/api';
import AppShell from '@/components/AppShell';
import { cn } from '@/lib/utils';

const STEPS_BY_EXAM = {
  celpip: [
    { task_type: 'listening_p1_problem_solving', label: 'Listening sampler', desc: 'One conversation under the real clock.' },
    { task_type: 'reading_p3_information', label: 'Reading sampler', desc: 'The most time-pressured reading part.' },
  ],
  pte: [
    { task_type: 'l_wfd', label: 'Dictation sampler', desc: 'The highest-leverage listening task: it feeds two skill scores.' },
    { task_type: 'rw_fib', label: 'Reading sampler', desc: 'Dropdown gaps that feed Reading and Writing both.' },
  ],
};

export default function Diagnostic() {
  const { exam } = useParams();
  const [progress, setProgress] = useState(null);

  useEffect(() => {
    api.progress(exam).then((res) => setProgress(res.data.progress)).catch(() => setProgress([]));
  }, [exam]);

  if (progress === null) return <AppShell><p className="text-slate-400">Loading…</p></AppShell>;

  const STEPS = STEPS_BY_EXAM[exam] || [];
  if (STEPS.length === 0) {
    return (
      <AppShell>
        <p className="mono-stat">{exam} · entry diagnostic</p>
        <h1 className="h-display mt-3 text-4xl">Coming with this exam's full bank.</h1>
        <p className="mt-3 max-w-xl text-[15px] leading-relaxed text-slate-500">
          The diagnostic activates when this exam's listening and reading banks land. Writing
          tasks are already open under Practice.
        </p>
        <Link to={`/${exam}/practice`} className="cta mt-6">To practice <ArrowRight className="h-4 w-4" /></Link>
      </AppShell>
    );
  }

  const statFor = (tt) => progress.find((p) => p.task_type === tt);
  const doneSteps = STEPS.filter((s) => statFor(s.task_type));
  const allDone = doneSteps.length === STEPS.length;

  // Recommendation: weakest attempted areas first, then untouched sections.
  const attempted = progress.filter((p) => p.accuracy != null).sort((a, b) => a.accuracy - b.accuracy);
  const weakest = attempted[0];

  return (
    <AppShell>
      <p className="mono-stat">{exam} · entry diagnostic</p>
      <h1 className="h-display mt-3 text-4xl">A quick baseline (optional).</h1>
      <p className="mt-3 max-w-xl text-[15px] leading-relaxed text-slate-500">
        Two short timed samplers show where you stand right now. Your path still covers every task, this just gives you a starting read so you know where to spend extra reps. You set the pace.
      </p>

      <div className="mt-10 grid border border-slate-200 bg-white sm:grid-cols-2">
        {STEPS.map((step, i) => {
          const stat = statFor(step.task_type);
          return (
            <div key={step.task_type} className={cn('p-7', i > 0 && 'border-t border-slate-200 sm:border-l sm:border-t-0')}>
              <div className="flex items-center gap-2">
                {stat ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Circle className="h-4 w-4 text-slate-300" />}
                <p className="mono-stat">step {i + 1}</p>
              </div>
              <h2 className="h-display mt-2 text-xl">{step.label}</h2>
              <p className="mt-1 text-sm text-slate-500">{step.desc}</p>
              {stat ? (
                <p className="mt-4 h-display text-3xl tabular-nums">
                  {Math.round((stat.accuracy ?? 0) * 100)}%
                </p>
              ) : (
                <Link to={`/${exam}/practice/${step.task_type}`} className="cta mt-5" data-testid={`diagnostic-step-${i}`}>
                  Start <ArrowRight className="h-4 w-4" />
                </Link>
              )}
            </div>
          );
        })}
      </div>

      {allDone && (
        <div className="mt-8 border border-slate-200 bg-white p-7" data-testid="diagnostic-recommendation">
          <p className="mono-stat">a starting read</p>
          {weakest ? (
            <>
              <h2 className="h-display mt-2 text-xl">
                Weakest right now: {weakest.task_type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}.
              </h2>
              <p className="mt-2 max-w-xl text-sm leading-relaxed text-slate-600">
                That sampler came in at {Math.round(weakest.accuracy * 100)}%, so give it extra reps. But this
                is only a baseline, work through every lesson in order; nothing is optional to cover. Your
                dashboard map tracks how much of each skill you've done.
              </p>
              <div className="mt-5 flex gap-3">
                <Link to={`/${exam}/lessons`} className="cta">Open the lessons <ArrowRight className="h-4 w-4" /></Link>
                <Link to={`/${exam}/practice`} className="cta-quiet">All practice</Link>
              </div>
            </>
          ) : (
            <p className="mt-2 text-sm text-slate-600">Complete the samplers to get your direction.</p>
          )}
        </div>
      )}
    </AppShell>
  );
}
